import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import LocationPicker from '../components/LocationTree';
import { resizeImage } from '../utils/resizeImage';
import './LocationForm.css';

function LocationForm() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEditMode = !!id;
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image: null,
    parentLocationId: searchParams.get('parentId') || ''
  });
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(isEditMode);
  const fileInputRef = useRef();
  const videoRef = useRef();
  const streamRef = useRef(null);
  const canvasRef = useRef();
  const [useCamera, setUseCamera] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const resized = await resizeImage(e.target.result);
          setFormData(prev => ({
            ...prev,
            image: resized
          }));
          setPreview(resized);
        } catch (err) {
          setError('Unable to read image');
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const startCamera = async () => {
    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      setUseCamera(true);
    } catch (err) {
      setError('Unable to access camera');
      console.error(err);
    }
  };

  // The <video> element only exists once useCamera is true, so attach the stream after that render
  useEffect(() => {
    if (useCamera && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [useCamera]);

  // Release the camera if the page is left while it is on
  useEffect(() => () => {
    if (streamRef.current) streamRef.current.getTracks().forEach(track => track.stop());
  }, []);

  const capturePhoto = async () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    // Match the camera's real aspect ratio so the photo isn't stretched to 4:3
    canvas.width = video.videoWidth || canvas.width;
    canvas.height = video.videoHeight || canvas.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = await resizeImage(canvas.toDataURL('image/jpeg'));
    setFormData(prev => ({
      ...prev,
      image: imageData
    }));
    setPreview(imageData);
    stopCamera();
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setUseCamera(false);
  };

  useEffect(() => {
    if (isEditMode && id) {
      fetchLocationData();
    }
  }, [id, isEditMode]);

  const fetchLocationData = async () => {
    try {
      const response = await fetch(`/api/locations/${id}`);
      const data = await response.json();
      setFormData({
        name: data.name || '',
        description: data.description || '',
        image: data.image ? `data:${data.imageMimeType};base64,${data.image}` : null,
        parentLocationId: data.parentLocationId || ''
      });
      if (data.image) {
        setPreview(`data:${data.imageMimeType};base64,${data.image}`);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching location:', err);
      setError('Failed to load location');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError('Location name is required');
      return;
    }

    try {
      const url = isEditMode ? `/api/locations/${id}` : '/api/locations';
      const method = isEditMode ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          image: formData.image ? formData.image.split(',')[1] : null,
          imageMimeType: formData.image ? 'image/jpeg' : null,
          parentLocationId: formData.parentLocationId || null
        })
      });

      if (response.ok) {
        navigate(formData.parentLocationId ? `/locations/${formData.parentLocationId}` : '/locations');
      } else {
        const errorData = await response.json();
        setError(errorData.error || `Failed to ${isEditMode ? 'update' : 'create'} location`);
      }
    } catch (err) {
      setError(`Error ${isEditMode ? 'updating' : 'creating'} location: ` + err.message);
    }
  };

  const noun = formData.parentLocationId ? 'Compartment' : 'Storage Location';

  return (
    <div className="form-container">
      <div className="form-card">
        <h1>{isEditMode ? `Edit ${noun}` : `Create ${noun}`}</h1>
        
        {loading && <p>Loading location...</p>}
        {error && <div className="error-message">{error}</div>}

        {!loading && (
          <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>{noun} Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="e.g., RV Bedroom Storage"
              required
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder={`Describe this ${noun.toLowerCase()}...`}
              rows="3"
            ></textarea>
          </div>

          <div className="form-group">
            <label>Inside (Optional: leave empty for a top-level storage location)</label>
            <LocationPicker
              value={formData.parentLocationId}
              onChange={(locationId) => setFormData(prev => ({ ...prev, parentLocationId: locationId }))}
              topLabel="Top level (a storage location)"
              excludeIds={id ? [id] : []}
              title="Inside"
            />
          </div>

          <div className="form-group">
            <label>{noun} Image</label>
            
            {useCamera ? (
              <div className="camera-section">
                <video
                  ref={videoRef}
                  autoPlay
                  muted
                  playsInline
                  className="video-preview"
                ></video>
                <canvas
                  ref={canvasRef}
                  width="640"
                  height="480"
                  style={{ display: 'none' }}
                ></canvas>
                <div className="camera-buttons">
                  <button type="button" onClick={capturePhoto} className="btn btn-primary">
                    Capture Photo
                  </button>
                  <button type="button" onClick={stopCamera} className="btn btn-secondary">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                {preview && (
                  <div className="image-preview">
                    <img src={preview} alt="Location" />
                    <button
                      type="button"
                      onClick={() => {
                        setPreview(null);
                        setFormData(prev => ({ ...prev, image: null }));
                      }}
                      className="remove-btn"
                    >
                      ✕
                    </button>
                  </div>
                )}
                <div className="image-upload">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current.click()}
                    className="btn btn-secondary"
                  >
                    Upload Photo
                  </button>
                  <button
                    type="button"
                    onClick={startCamera}
                    className="btn btn-secondary"
                  >
                    Take Photo
                  </button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
              </>
            )}
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary">
              {isEditMode ? `Update ${noun}` : `Create ${noun}`}
            </button>
            <button
              type="button"
              onClick={() => navigate(formData.parentLocationId ? `/locations/${formData.parentLocationId}` : '/locations')}
              className="btn btn-secondary"
            >
              Cancel
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}

export default LocationForm;
